import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { faCircle } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { Box, Fade, Stack, SxProps, Theme } from "@mui/material";
import { useDrag } from "@use-gesture/react";
import { ReactElement, useMemo, useRef } from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { IconButton } from "../MediaControl/IconButton";

export type MediaNavigationProps = {
  onNext: (() => void) | null;
  onPrevious: (() => void) | null;
  onMediaClick?: () => void;
  sx?: SxProps<Theme>;
  hidden?: boolean;
  playing?: boolean;
  fullscreenMode?: boolean;
  showFullscreenControl?: boolean;
  Media: ReactElement;
  Summary?: ReactElement | null;
  Controls?: ReactElement | null;
};

export type CarouselIndicatorProps = {
  position: number;
  totalCount: number;
  sx?: SxProps<Theme>;
};

const MAX_INDICATORS = 9;

const colorForIndex = (index: number, value: number, theme: Theme): string => {
  const { palette } = theme.vars;

  if (index === value) return palette.common.white;
  if (index === value + 1 || index === value - 1) return palette.grey.A400;
  if ([value - 3, value - 2, value + 2, value + 3].includes(index)) return palette.grey.A300;
  return palette.grey.A200;
};

export const CarouselIndicator = ({
  position,
  totalCount,
  sx,
}: CarouselIndicatorProps): ReactElement => {
  const dots = useMemo(
    () => Array.from({ length: Math.min(totalCount, MAX_INDICATORS) }, (_, i) => i + 1),
    [totalCount],
  );
  const value = useMemo(() => {
    if (position < 1) return 1;

    const halfMax = MAX_INDICATORS / 2;

    if (totalCount <= MAX_INDICATORS || position < halfMax) {
      return position;
    }

    if (position >= totalCount) {
      return MAX_INDICATORS;
    }

    const halfTotal = totalCount / 2;
    const remainder = totalCount % position;
    if (position > halfTotal && remainder < Math.floor(halfMax)) {
      return MAX_INDICATORS - remainder;
    }

    return Math.ceil(halfMax);
  }, [position, totalCount]);

  return (
    <Stack
      className="MuiCarouselIndicator-root"
      direction="row"
      alignItems="center"
      justifyContent="center"
      width="100%"
      gap={0.5}
      minHeight={24}
      sx={sx}
    >
      {dots.map((index) => (
        <FaIcon
          key={index}
          icon={faCircle}
          sx={{
            width: 5,
            height: 5,
            color: (theme) => colorForIndex(index, value, theme),
          }}
          className={`MuiCarouselIndicator-dot MuiCarouselIndicator-${index}`}
        />
      ))}
    </Stack>
  );
};

export const MediaNavigation = ({
  onNext,
  onPrevious,
  onMediaClick,
  hidden,
  playing,
  fullscreenMode,
  showFullscreenControl,
  sx,
  Media,
  Summary = null,
  Controls = null,
}: MediaNavigationProps): ReactElement => {
  const mediaRootRef = useRef<HTMLDivElement | null>(null);

  const bind = useDrag(({ active, movement: [mx], direction: [xDir], cancel }) => {
    const width = mediaRootRef.current?.offsetWidth;

    if (width && active && Math.abs(mx) > width / 2) {
      const isSwipePrev = xDir > 0;

      if (isSwipePrev) {
        onPrevious?.();
      } else {
        onNext?.();
      }

      cancel();
    }
  });

  return (
    <Stack className="MuiMediaNavigation-root" position="relative" sx={sx}>
      <Stack
        className="MuiMediaNavigation-mediaRoot"
        flex={1}
        gap={1}
        direction="row"
        alignItems="center"
      >
        <Box
          className="MuiMediaNavigation-previousButtonRoot"
          sx={{
            display: { xs: "none", sm: "block" },
            ...((hidden || !onPrevious) && { visibility: { sm: "hidden" } }),
          }}
        >
          <IconButton
            className="MuiMediaNavigation-previousButton"
            size="medium"
            onClick={() => onPrevious?.()}
          >
            <FaIcon icon={faChevronLeft} />
          </IconButton>
        </Box>
        <Box
          className="MuiMediaNavigation-media"
          ref={mediaRootRef}
          onClick={onMediaClick}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            touchAction: "none",
            height: 1,
            position: "relative",
            img: { position: "absolute" },
            "img, video": (theme) => ({
              maxWidth: "100%",
              maxHeight: {
                xs: "100%",
                sm: `calc(100% - ${theme.spacing(12)})`,
              },
            }),
          }}
          {...bind()}
        >
          {Media}
        </Box>
        <Box
          className="MuiMediaNavigation-nextButtonRoot"
          sx={{
            display: { xs: "none", sm: "block" },
            ...((hidden || !onNext) && { visibility: { sm: "hidden" } }),
          }}
        >
          <IconButton
            className="MuiMediaNavigation-nextButton"
            size="medium"
            onClick={() => onNext?.()}
          >
            <FaIcon icon={faChevronRight} />
          </IconButton>
        </Box>
      </Stack>
      {Summary}
      <Fade in={!fullscreenMode || showFullscreenControl} timeout={fullscreenMode ? 600 : 0}>
        <Box
          className="MuiMediaNavigation-controlsRoot"
          sx={{
            inset: 0,
            top: "unset",
            backdropFilter: { xs: "blur(8px)", sm: "unset" },
            backgroundColor: { xs: "rgba(0,0,0,0.2)", sm: "unset" },
            position: { xs: "absolute", sm: "unset" },
            ...((hidden || (playing && !fullscreenMode)) && {
              display: { xs: "none", sm: "unset" },
            }),
          }}
        >
          {Controls}
        </Box>
      </Fade>
    </Stack>
  );
};
